import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dashboardDetailsAPI, leadAPI, followUpAPI, followUpSnooze } from '../api';
import { toast } from 'sonner';
import { ArrowLeft, Phone, MessageSquare, Eye, Edit, CheckCircle, Clock, Snowflake } from 'lucide-react';
import { format } from 'date-fns';
import { ClickToCallButton } from '../components/ClickToCallButton';

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const CARDS = {
  'new-leads-today': { title: 'New Leads Today', desc: 'Leads created today', api: 'newLeadsToday' },
  'hot-leads': { title: 'Hot Leads', desc: 'Highly qualified leads', api: 'hotLeads' },
  'followups-today': { title: 'Follow-ups Today', desc: 'Today\'s scheduled follow-ups', api: 'followupsToday' },
  'missed-followups': { title: 'Missed Follow-ups', desc: 'Overdue follow-ups requiring action', api: 'missedFollowups' },
  'revenue': { title: 'Total Revenue', desc: 'Revenue breakdown by period and dimension', api: 'revenue' }
};

const Table = ({ headers, rows, testId }) => (
  <div className="clay-card overflow-hidden" data-testid={testId}>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
          <tr>{headers.map((h, i) => <th key={i} className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={headers.length} className="text-center py-6 text-muted-foreground">No records</td></tr>
          ) : rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30">
              {r.map((c, j) => <td key={j} className="px-4 py-3">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const DashboardDetailPage = () => {
  const { cardKey } = useParams();
  const navigate = useNavigate();
  const cfg = CARDS[cardKey];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!cfg) return;
    setLoading(true);
    try {
      const { data: res } = await dashboardDetailsAPI[cfg.api]();
      setData(res);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('You do not have permission to view detailed reports');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [cfg, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleComplete = async (id) => {
    try { await followUpAPI.update(id, { completed: true, outcome: 'Completed' }); toast.success('Marked complete'); fetchData(); } catch { toast.error('Failed'); }
  };
  const handleSnooze = async (id, mins) => {
    try { await followUpSnooze(id, mins); toast.success(`Snoozed ${mins}m`); fetchData(); } catch { toast.error('Failed'); }
  };

  if (!cfg) return <div className="p-6">Unknown card: {cardKey}</div>;
  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div></div>;

  const renderLeads = (leads) => (
    <Table
      testId="detail-leads-table"
      headers={['Customer', 'Mobile', 'City', 'Service', 'Source', 'Assigned', 'Created', 'Priority', 'Status', 'Revenue', 'Actions']}
      rows={(leads || []).map(l => [
        <button key="n" onClick={() => navigate(`/leads/${l.id}`)} className="text-primary hover:underline">{l.customer_name}</button>,
        l.primary_mobile, l.city || '—',
        (l.services_required || []).join(', ') || '—',
        l.lead_source, l.assigned_to_name || '—',
        l.created_at ? format(new Date(l.created_at), 'MMM d, h:mm a') : '—',
        <span key="p" className={`text-xs px-2 py-0.5 rounded ${l.priority === 'urgent' ? 'bg-red-100 text-red-700' : l.priority === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-700'}`}>{(l.priority || '').toUpperCase()}</span>,
        <span key="s" className="text-xs px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800">{(l.status || '').replace(/_/g, ' ')}</span>,
        fmt(l.budget),
        <div key="a" className="flex gap-1">
          <ClickToCallButton lead={l} stopPropagation iconOnly dataTestid="detail-call-btn" className="p-1.5 bg-green-50 text-green-700 rounded hover:bg-green-100 disabled:opacity-60" />
          <a href={`https://wa.me/${l.whatsapp_number || l.primary_mobile}`} target="_blank" rel="noreferrer" className="p-1.5 bg-green-50 text-green-700 rounded hover:bg-green-100" title="WhatsApp"><MessageSquare className="h-3 w-3" /></a>
          <button onClick={() => navigate(`/leads/${l.id}`)} className="p-1.5 bg-primary/10 text-primary rounded hover:bg-primary/20" title="View"><Eye className="h-3 w-3" /></button>
          <button onClick={() => navigate(`/leads/${l.id}/edit`)} className="p-1.5 bg-slate-100 dark:bg-slate-800 rounded" title="Edit"><Edit className="h-3 w-3" /></button>
        </div>
      ])}
    />
  );

  const renderHotLeads = (leads) => (
    <Table
      testId="detail-hot-leads-table"
      headers={['Customer', 'Service', 'Expected Rev', 'Assigned', 'Last F/U', 'Next F/U', 'Probability', 'Stage', 'Actions']}
      rows={(leads || []).map(l => [
        <button key="n" onClick={() => navigate(`/leads/${l.id}`)} className="text-primary hover:underline">{l.customer_name}</button>,
        (l.services_required || []).join(', ') || '—',
        fmt(l.budget), l.assigned_to_name || '—',
        l.last_follow_up ? format(new Date(l.last_follow_up), 'MMM d') : '—',
        l.next_follow_up ? format(new Date(l.next_follow_up), 'MMM d, h:mm a') : '—',
        `${l.probability || 0}%`,
        <span key="s" className="text-xs px-2 py-0.5 rounded bg-orange-100 text-orange-700">{(l.status || '').replace(/_/g, ' ')}</span>,
        <div key="a" className="flex gap-1">
          <ClickToCallButton lead={l} stopPropagation iconOnly dataTestid="detail-hot-call-btn" className="p-1.5 bg-green-50 text-green-700 rounded disabled:opacity-60" />
          <a href={`https://wa.me/${l.whatsapp_number || l.primary_mobile}`} target="_blank" rel="noreferrer" className="p-1.5 bg-green-50 text-green-700 rounded"><MessageSquare className="h-3 w-3" /></a>
        </div>
      ])}
    />
  );

  const renderFollowups = (list, isMissed) => (
    <Table
      testId={isMissed ? 'detail-missed-table' : 'detail-followups-table'}
      headers={isMissed
        ? ['Customer', 'Assigned', 'Missed', 'Days Overdue', 'Priority', 'Notes', 'Actions']
        : ['Customer', 'Assigned', 'Reminder', 'Service', 'Priority', 'Notes', 'Actions']
      }
      rows={(list || []).map(f => isMissed ? [
        <button key="n" onClick={() => navigate(`/leads/${f.lead_id}`)} className="text-primary hover:underline">{f.customer_name}</button>,
        f.assigned_user || '—',
        f.missed_date ? format(new Date(f.missed_date), 'MMM d, h:mm a') : '—',
        <span key="d" className={`text-xs px-2 py-0.5 rounded ${f.days_overdue > 3 ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{f.days_overdue}d</span>,
        <span key="p" className="text-xs px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800">{(f.priority || '').toUpperCase()}</span>,
        f.notes || '—',
        <div key="a" className="flex gap-1">
          <ClickToCallButton lead={f} stopPropagation iconOnly dataTestid="detail-missed-call-btn" className="p-1.5 bg-green-50 text-green-700 rounded disabled:opacity-60" />
          <a href={`https://wa.me/${f.whatsapp_number || f.primary_mobile}`} target="_blank" rel="noreferrer" className="p-1.5 bg-green-50 text-green-700 rounded"><MessageSquare className="h-3 w-3" /></a>
          <button onClick={() => handleComplete(f.id)} className="p-1.5 bg-green-100 text-green-700 rounded" title="Complete"><CheckCircle className="h-3 w-3" /></button>
          <button onClick={() => navigate('/calendar')} className="p-1.5 bg-slate-100 dark:bg-slate-800 rounded" title="Reschedule"><Clock className="h-3 w-3" /></button>
        </div>
      ] : [
        <button key="n" onClick={() => navigate(`/leads/${f.lead_id}`)} className="text-primary hover:underline">{f.customer_name}</button>,
        f.assigned_user || '—',
        format(new Date(f.reminder_time), 'h:mm a'),
        f.service || '—',
        <span key="p" className="text-xs px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800">{(f.priority || '').toUpperCase()}</span>,
        f.notes || '—',
        <div key="a" className="flex gap-1">
          <ClickToCallButton lead={f} stopPropagation iconOnly dataTestid="detail-fu-call-btn" className="p-1.5 bg-green-50 text-green-700 rounded disabled:opacity-60" />
          <a href={`https://wa.me/${f.whatsapp_number || f.primary_mobile}`} target="_blank" rel="noreferrer" className="p-1.5 bg-green-50 text-green-700 rounded"><MessageSquare className="h-3 w-3" /></a>
          <button onClick={() => handleComplete(f.id)} className="p-1.5 bg-green-100 text-green-700 rounded" title="Complete"><CheckCircle className="h-3 w-3" /></button>
          <button onClick={() => handleSnooze(f.id, 60)} className="p-1.5 bg-yellow-100 text-yellow-700 rounded" title="Snooze 1h"><Snowflake className="h-3 w-3" /></button>
        </div>
      ])}
    />
  );

  const renderRevenue = (r) => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[['Today', r.today], ['Weekly', r.weekly], ['Monthly', r.monthly], ['Quarterly', r.quarterly], ['Yearly', r.yearly]].map(([k, v]) => (
          <div key={k} className="clay-card p-4">
            <p className="text-sm text-muted-foreground">{k}</p>
            <p className="text-xl font-outfit font-semibold mt-1">{fmt(v)}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          ['By Employee', r.by_employee, 'rev-by-employee'],
          ['By Manager', r.by_manager, 'rev-by-manager'],
          ['By Service', r.by_service, 'rev-by-service'],
          ['By Lead Source', r.by_source, 'rev-by-source'],
          ['By Project Type', r.by_project_type, 'rev-by-project-type'],
          ['By Customer (Top 20)', r.by_customer, 'rev-by-customer']
        ].map(([title, list, tid]) => (
          <Table
            key={title}
            testId={tid}
            headers={[title, 'Revenue']}
            rows={(list || []).map(e => [e.name, fmt(e.value)])}
          />
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="dashboard-detail-container">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="detail-back-btn">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-3xl font-outfit font-semibold">{cfg.title}</h1>
          <p className="text-muted-foreground">{cfg.desc}</p>
        </div>
        {data?.count != null && (
          <div className="clay-card px-4 py-2">
            <p className="text-xs text-muted-foreground">Total</p>
            <p className="text-xl font-outfit font-semibold">{data.count}</p>
          </div>
        )}
      </div>

      {cardKey === 'new-leads-today' && renderLeads(data?.leads)}
      {cardKey === 'hot-leads' && renderHotLeads(data?.leads)}
      {cardKey === 'followups-today' && renderFollowups(data?.followups, false)}
      {cardKey === 'missed-followups' && renderFollowups(data?.followups, true)}
      {cardKey === 'revenue' && data && renderRevenue(data)}
    </div>
  );
};

export default DashboardDetailPage;
